import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GPA_LOGO } from "@/lib/assets";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Lock, Mail, ArrowRight, CheckCircle2 } from "lucide-react";

const DOMAINS = ["@lonestar.edu", "@eddesignlab.org", "@wastelinq.com"];

export default function Access() {
  return (
    <div className="brand-hero relative min-h-screen overflow-hidden text-white">
      <div className="brand-mesh absolute inset-0" />
      <div className="hero-noise pointer-events-none absolute inset-0" />
      <div className="relative mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-6 py-16 text-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center rounded-2xl bg-white px-4 py-3 shadow-lg">
            <img src={GPA_LOGO} alt="GPA+" className="h-12 w-auto" />
          </span>
        </motion.div>

        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#a8e063]">
          <Lock className="h-3.5 w-3.5" /> Private preview · Team access only
        </motion.span>

        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mt-6 font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
          GPA+ is almost ready.
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
          className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
          GPA+ is Lone Star College's Transferable Skills Benchmark — a new way to measure, grow, and verify
          the durable human skills that a GPA can't show. This is a private preview for our team and partners as
          we put the finishing touches on the experience. We can't wait to share it with the world soon.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <Link to="/login">
            <Button data-testid="access-signin" size="lg" className="rounded-full bg-[#78be21] px-8 text-base font-bold text-[#00243f] hover:bg-[#6aac1c]">
              Sign in <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <Link to="/signup">
            <Button data-testid="access-request" size="lg" variant="outline" className="rounded-full border-white/30 bg-white/5 px-8 text-base font-bold text-white hover:bg-white/10">
              Create an account
            </Button>
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.44 }}
          className="mt-8 w-full max-w-xl rounded-2xl border border-white/10 bg-white/5 p-5 text-left backdrop-blur-sm">
          <p className="text-xs font-bold uppercase tracking-widest text-[#a8e063]">Approved members</p>
          <p className="mt-2 text-sm text-slate-300">Accounts can currently be created only with an email from an approved organization:</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {DOMAINS.map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-white">
                <CheckCircle2 className="h-3.5 w-3.5 text-[#78be21]" /> {d}
              </span>
            ))}
          </div>
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-300">
            <Mail className="h-4 w-4 text-[#a8e063]" /> Tech support or to request access:{" "}
            <a href="mailto:jeff.hastings@lonestar.edu" className="font-semibold text-white underline">jeff.hastings@lonestar.edu</a>
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.52 }}
          className="mt-6 flex max-w-xl items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-left" data-testid="access-disclaimer">
          <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-300" />
          <p className="text-sm leading-relaxed text-amber-100">
            <strong>Confidential.</strong> This site and its contents are a private preview. Access by unauthorized
            individuals is strictly prohibited. By continuing you confirm you are an approved member or partner.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
